import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, RadialBarChart, RadialBar, Legend, LineChart, Line, CartesianGrid } from "recharts";

export default function AnalyticsPage() {
    const q = useQuery({ queryKey: ["analytics"], queryFn: () => api.get("/analytics").then((r) => r.data) });
    const d = q.data;

    if (q.isLoading || !d) return <div className="text-textmuted">Caricamento analytics…</div>;

    return (
        <div className="space-y-8 pv-enter">
            <div>
                <div className="pv-eyebrow mb-2">Analytics</div>
                <h1 className="pv-serif text-4xl tracking-tight">Come stiamo crescendo</h1>
                <p className="text-[13.5px] text-textmuted mt-2 max-w-2xl">
                    Confronta canali, contenuti, campagne. Scopri cosa produce clienti veri (non solo like).
                </p>
            </div>

            <div className="grid md:grid-cols-4 gap-3">
                <KPI label="CAC medio" value={`€${d.cac}`} sub="Costo acquisizione cliente" />
                <KPI label="LTV" value={`€${d.ltv.toLocaleString("it-IT")}`} sub="Valore vita cliente" />
                <KPI label="Churn" value={`${d.churn_pct}%`} sub="Perdita clienti" tone={d.churn_pct > 8 ? "critical" : "success"} />
                <KPI label="Retention" value={`${d.retention_pct}%`} sub="Clienti mantenuti" tone="success" />
            </div>

            <div className="pv-card p-6">
                <div className="text-[15px] font-medium mb-1">Performance canali</div>
                <div className="text-[11.5px] text-textmuted mb-4">Ogni canale ordinato per numero di clienti generati</div>
                <div className="h-72">
                    <ResponsiveContainer>
                        <BarChart data={d.channel_performance} layout="vertical" margin={{ left: 40, right: 20 }}>
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="source" tick={{ fill: "rgb(148 163 184)", fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                            <Tooltip />
                            <Bar dataKey="leads" fill="#38bdf8" name="Lead" radius={[0, 4, 4, 0]} isAnimationActive />
                            <Bar dataKey="clients" fill="#0c4ad9" name="Clienti" radius={[0, 4, 4, 0]} isAnimationActive />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="pv-card p-6">
                    <div className="text-[15px] font-medium mb-1">Webinar → risultati reali</div>
                    <div className="text-[11.5px] text-textmuted mb-4">Non solo partecipanti: quante demo e clienti hanno prodotto</div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={d.webinars} margin={{ left: 6, right: 6, top: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgb(148 163 184 / 0.15)" />
                                <XAxis dataKey="title" tick={{ fill: "rgb(148 163 184)", fontSize: 9 }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={60} />
                                <YAxis hide />
                                <Tooltip />
                                <Bar dataKey="attended" fill="#38bdf8" name="Presenti" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="demos" fill="#0c4ad9" name="Demo" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="clients" fill="#a855f7" name="Clienti" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="pv-card p-6">
                    <div className="text-[15px] font-medium mb-1">Newsletter — per motivazione</div>
                    <div className="text-[11.5px] text-textmuted mb-4">Quale tono psicologico converte di più</div>
                    <div className="space-y-3">
                        {d.newsletters.map((n, i) => (
                            <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[12.5px] truncate">{n.subject}</div>
                                        <div className="text-[10.5px] text-textsubtle">{n.motivation}</div>
                                    </div>
                                    <span className="text-[11px] pv-mono text-textmuted ml-3">{n.open_rate}% · CTR {n.ctr}%</span>
                                </div>
                                <div className="h-1.5 bg-surface2 rounded-full overflow-hidden">
                                    <div className="h-full bg-primary" style={{ width: `${n.open_rate}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function KPI({ label, value, sub, tone }) {
    const cls = tone === "critical" ? "text-critical" : "text-textmain";
    return (
        <div className="pv-card p-5 pv-hover">
            <div className="text-[11px] uppercase tracking-wider text-textsubtle">{label}</div>
            <div className={`text-3xl font-medium pv-serif mt-1 ${cls}`}>{value}</div>
            <div className="text-[11.5px] text-textmuted mt-0.5">{sub}</div>
        </div>
    );
}
